import Link from "@/components/ui/intent-link";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

import { cn } from "@/lib/utils";

const VARIANTS = {
  teal: "bg-teal-07 text-white hover:bg-teal-08",
  navy: "bg-navy-03 text-white hover:bg-navy-02",
  white: "bg-white text-navy-03 hover:bg-teal-03",
  orange: "bg-brand-orange-02 text-white hover:bg-brand-orange-03",
} as const;

const SELECTED = {
  teal: "bg-teal-08",
  navy: "bg-navy-02",
  white: "bg-teal-03",
  orange: "bg-brand-orange-03",
} as const;

export type ButtonVariant = keyof typeof VARIANTS;

const BASE =
  "inline-flex h-14 shrink-0 cursor-pointer items-center justify-center rounded-lg px-7 text-button font-bold whitespace-nowrap transition-colors disabled:pointer-events-none disabled:opacity-50";

type CommonProps = {
  variant?: ButtonVariant;
  selected?: boolean;
  className?: string;
  children: ReactNode;
};

export function buttonClasses({
  variant = "teal",
  selected = false,
  className,
}: Omit<CommonProps, "children"> = {}): string {
  return cn(BASE, VARIANTS[variant], selected && SELECTED[variant], className);
}

type ButtonProps = CommonProps & Omit<ComponentPropsWithoutRef<"button">, "className" | "children">;

export function Button({
  variant,
  selected,
  className,
  children,
  type = "button",
  ...rest
}: ButtonProps) {
  return (
    <button type={type} className={buttonClasses({ variant, selected, className })} {...rest}>
      {children}
    </button>
  );
}

type ButtonLinkProps = CommonProps &
  Omit<ComponentPropsWithoutRef<typeof Link>, "className" | "children">;

export function ButtonLink({ variant, selected, className, children, ...rest }: ButtonLinkProps) {
  return (
    <Link className={buttonClasses({ variant, selected, className })} {...rest}>
      {children}
    </Link>
  );
}
